import { ComponentType } from '@angular/cdk/portal';
import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  MatSnackBar,
  MatSnackBarRef,
  TextOnlySnackBar,
} from '@angular/material/snack-bar';
import { InputDialogComponent } from '../input-dialog/input-dialog.component';
import { DialogAlertComponent, DialogAlertData } from '../dialog-alert/dialog-alert.component';
import { InputDialogConfig } from '../models/input-dialog-config';
import { ErrorReferenceService } from '../observability/error-reference.service';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private activeSnackbar?: MatSnackBarRef<TextOnlySnackBar>;
  private readonly errorReference = inject(ErrorReferenceService);

  constructor(
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
  ) {}

  public InputDialog(
    detailConfig: InputDialogConfig,
    afterClosed: (data: string) => void,
    config?: MatDialogConfig,
  ): void {
    const dialogRef = this.dialog.open(InputDialogComponent, {
      data: detailConfig,
      panelClass: 'main-panel',
      ...config,
    });
    if (afterClosed != null)
      dialogRef.afterClosed().subscribe((data: string) => afterClosed(data));
  }

  public SmallDialog(
    message: string,
    action: string = 'Close',
    delay: number = 60000,
  ): void {
    this.ClearSmallDialog();
    this.activeSnackbar = this.snackbar.open(message, action, {
      duration: delay,
    });
  }

  public DialogWindow<T, R>(
    component: ComponentType<unknown>,
    dialogData: T,
    afterClosed: (data: R) => void,
    config?: MatDialogConfig,
  ): void {
    const dialogRef = this.dialog.open(component, {
      data: dialogData,
      panelClass: 'main-panel',
      ...config,
      autoFocus: false,
    });
    if (afterClosed != null)
      dialogRef.afterClosed().subscribe((data: R) => afterClosed(data));
  }

  public AlertDialogWindow(
    reason: string,
    options: Omit<DialogAlertData, 'reason'> = {},
  ): void {
    this.DialogWindow<DialogAlertData, void>(
      DialogAlertComponent,
      { reason, ...options },
      () => {},
      { width: '420px', maxWidth: 'calc(100vw - 32px)' },
    );
  }

  /**
   * Standard "you can't use this feature" dialog, shown when a guard or a
   * merchant switch lands the user on something their account can't access.
   */
  public AccessDeniedDialog(featureName: string): void {
    this.AlertDialogWindow(
      `Your account doesn't include ${featureName} for the selected merchant. ` +
        'Ask your administrator to grant access if you need it.',
      {
        title: `${featureName} isn't available`,
        icon: 'lock',
        action: 'OK',
      },
    );
  }

  public ClearSmallDialog(): void {
    this.activeSnackbar?.dismiss();
  }

  /** Error toast after a failed call; adds a copyable reference when tracing is on. */
  public ErrorSmallDialog(message: string, error?: unknown): void {
    this.ClearSmallDialog();
    this.activeSnackbar = this.errorReference.openErrorSnackbar(message, error);
  }

  /** Error dialog after a failed call; adds a copyable reference when tracing is on. */
  public ErrorAlertDialog(
    reason: string,
    error?: unknown,
    options: Omit<DialogAlertData, 'reason' | 'reference'> = {},
  ): void {
    const reference = this.errorReference.referenceFor(error);
    this.AlertDialogWindow(reference ? `${reason} Reference: ${reference}` : reason, {
      tone: 'error',
      ...options,
      ...(reference ? { reference } : {}),
    });
  }
}
